import { useMemo } from 'react';

export type MobileOS = 'android' | 'ios' | null;
export type DesktopOS = 'mac' | 'windows' | 'linux' | null;

export type UseDeviceOSResult = {
  mobileOS: MobileOS;
  desktopOS: DesktopOS;
  isAndroid: boolean;
  isIOS: boolean;
  isMac: boolean;
  isWindows: boolean;
  isLinux: boolean;
  isTouchCapable: boolean;
};

export const useDeviceOS = (): UseDeviceOSResult => {
  return useMemo(() => {
    const hasNavigator = typeof navigator !== 'undefined';
    const hasWindow = typeof window !== 'undefined';

    if (!hasNavigator) {
      return {
        mobileOS: null,
        desktopOS: null,
        isAndroid: false,
        isIOS: false,
        isMac: false,
        isWindows: false,
        isLinux: false,
        isTouchCapable: false,
      };
    }

    const navAny = navigator as any;
    const ua: string = navigator.userAgent || '';
    const uaData = navAny.userAgentData || null;
    const platform: string = (uaData?.platform || navAny.platform || '') as string;

    const uaLower = ua.toLowerCase();
    const platformLower = String(platform).toLowerCase();

    const isTouchCapable = hasWindow && ('ontouchstart' in window || navAny.maxTouchPoints > 0);

    const isiPadOSMacUA = /macintosh/.test(uaLower) && isTouchCapable; // iPadOS 13+ masquerading as Mac
    const isIOSUA = /iphone|ipad|ipod/.test(uaLower) || isiPadOSMacUA;
    const isAndroidUA = /android/.test(uaLower);

    const isMacOS = /mac|macos/.test(platformLower) || /mac os x|macintosh/.test(uaLower);
    const isWindowsOS = /win/.test(platformLower) || /windows/.test(uaLower);
    const isLinuxOS = !isMacOS && !isWindowsOS && (/linux/.test(platformLower) || /linux|x11/.test(uaLower));

    const mobileOS: MobileOS = isIOSUA ? 'ios' : isAndroidUA ? 'android' : null;
    const desktopOS: DesktopOS = isMacOS ? 'mac' : isWindowsOS ? 'windows' : isLinuxOS ? 'linux' : null;

    return {
      mobileOS,
      desktopOS,
      isAndroid: mobileOS === 'android',
      isIOS: mobileOS === 'ios',
      isMac: desktopOS === 'mac',
      isWindows: desktopOS === 'windows',
      isLinux: desktopOS === 'linux',
      isTouchCapable,
    };
  }, []);
};

export default useDeviceOS;
